<template>
    <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-2">
            <div class="text-2xl font-medium text-gray-900">
                Recurring Billing Details
            </div>

            <prime-button
                text
                icon="pi pi-pencil"
                label="Edit"
                :class="editing ? 'invisible' : ''"
                :disabled="isProcessing"
                @click="handleEditButton"
            />
        </div>

        <div class="space-y-4">
            <div>
                <span class="font-semibold">Recurring Dues:</span
                >&nbsp;&nbsp;<span class="text-lg">{{
                    recurringTotal.formatCurrency()
                }}</span>
                <br />
                <div class="text-xs italic">
                    *Sales tax will be calculated at the time of billing
                </div>
            </div>

            <Transition name="collapse">
                <div
                    v-if="editing"
                    v-html="
                        storedPaymentInformation.recurringAuthorizationMarkup
                    "
                />
            </Transition>
        </div>

        <div class="max-w-[650px]">
            <div
                v-if="editing"
                class="mb-4 flex flex-col gap-2 md:mb-0 md:flex-row md:items-center md:justify-between"
            >
                <div class="text-lg font-medium">Billing method</div>

                <prime-button
                    v-if="
                        editing &&
                        addingPaymentMethod &&
                        storedPaymentMethods.length > 0
                    "
                    outlined
                    label="Use Existing Payment Method"
                    :disabled="isProcessing"
                    @click="handleCancelAddPaymentMethod"
                />
            </div>

            <div v-if="addingPaymentMethod">
                <new-recurring-method
                    ref="newRecurringMethodRef"
                    :payment-sources="paymentSources"
                    :error="errorRecurring"
                    @payment-method="handleRecurringPaymentMethod"
                />
            </div>

            <div
                v-else
                class="mb-4 mt-1 grid grid-cols-1 gap-4 md:grid-cols-2"
            >
                <payment-method-option
                    v-if="selectedPaymentMethod !== null"
                    :payment-method="selectedPaymentMethod"
                    :selected-token="selectedToken"
                    :editing="editing && displayAllPaymentMethods"
                    @select-card="displayAllPaymentMethods = false"
                />

                <template v-if="displayAllPaymentMethods">
                    <payment-method-option
                        v-for="paymentMethod in paymentMethods"
                        :key="paymentMethod.id"
                        :payment-method="paymentMethod"
                        :selected-token="selectedToken"
                        :editing="editing"
                        @select-card="handleSelectPaymentMethod(paymentMethod)"
                    />

                    <prime-button
                        outlined
                        class="flex h-[100px] justify-center border-dashed"
                        :disabled="isProcessing"
                        @click="handleAddPaymentMethod"
                    >
                        <span
                            class="flex items-center justify-center gap-1 font-bold"
                        >
                            <i class="pi pi-fw pi-plus" />
                            Add Payment Method
                        </span>
                    </prime-button>
                </template>

                <div
                    v-if="editing && !displayAllPaymentMethods"
                    class="flex flex-1 flex-col items-start gap-4 md:items-end"
                >
                    <prime-button
                        outlined
                        label="Change Payment Method"
                        class="whitespace-nowrap"
                        :disabled="isProcessing"
                        @click="displayAllPaymentMethods = true"
                    />
                </div>

                <div
                    v-if="editing"
                    class="text-red-600"
                >
                    {{ errorRecurring || '&nbsp;' }}
                </div>
            </div>

            <div
                v-if="showDefaultPaymentMethodNote"
                class="mt-2 text-sm italic"
            >
                *This will be your new default payment method for recurring and
                on-account purchases.
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { boolean, mixed, object, string } from 'yup'
import { Decimal } from '~/composables/support/decimal'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'

export const StoredPaymentInformationFacilityFragment = graphql(`
    fragment StoredPaymentInformationFacilityFragment on Facility {
        recurringAuthorizationMarkup(language: ENGLISH)
        cpxBankAccountStoredPaymentSource {
            ...NewRecurringMethodPaymentSourceFragment
        }
        cpxCreditCardStoredPaymentSource {
            ...NewRecurringMethodPaymentSourceFragment
        }
    }
`)

export const RecurringInformationCustomerFragment = graphql(/* GraphQL */ `
    fragment RecurringInformationCustomerFragment on Customer {
        ...BillingAddressCustomerFragment
        newStoredPaymentInformation(facilityId: $facilityId) {
            facility {
                ...StoredPaymentInformationFacilityFragment
            }
        }
        storedPaymentMethods {
            id
            formattedBrand
            formattedMaskedAccount
            isCreditCard
            isCustomerDefault
            lastUsedDate
            accountExpirationDate
            migrationRequired
            paymentTodayAllowed
            paymentSource {
                id
            }
        }
    }
`)

export const RecurringInformationFacilityFragment = graphql(/* GraphQL */ `
    fragment RecurringInformationFacilityFragment on Facility {
        ...StoredPaymentInformationFacilityFragment
        cpxPaymentSources {
            id
            data {
                __typename
                ... on PaymentSourceCreditCardCardpointe {
                    ...HostedPaymentFormPaymentSourceFragment
                }
            }
        }
    }
`)

export const RecurringInformationSchema = object({
    total: string().nullable(),
    paymentMethod: object()
        .shape({
            type: mixed().oneOf([
                'StoredPaymentMethod',
                'CreditCard',
                'BankAccount'
            ]),
            key: string(),
            paymentSourceId: string(),
            paymentTodayAllowed: boolean(),
            displayInfo: object().shape({
                brand: string(),
                lastFour: string(),
                accountExpirationDate: string().nullable(),
                lastUsedDate: string().nullable()
            }),

            // Saved payment method
            id: string().when('type', {
                is: 'StoredPaymentMethod',
                then: schema => schema.required(),
                otherwise: schema => schema.transform(() => undefined)
            }),

            // Credit Card
            token: string().when('type', {
                is: 'CreditCard',
                then: schema => schema.required(),
                otherwise: schema => schema.transform(() => undefined)
            }),

            // Bank account
            routingNumber: string().when('type', {
                is: 'BankAccount',
                then: schema => schema.required(),
                otherwise: schema => schema.transform(() => undefined)
            }),
            accountNumber: string().when('type', {
                is: 'BankAccount',
                then: schema => schema.required(),
                otherwise: schema => schema.transform(() => undefined)
            }),
            accountNumberConfirm: string().when('type', {
                is: 'BankAccount',
                then: schema => schema.required(),
                otherwise: schema => schema.transform(() => undefined)
            }),
            accountType: string().when('type', {
                is: 'BankAccount',
                then: schema => schema.required(),
                otherwise: schema => schema.transform(() => undefined)
            })
        })
        .nullable()
        .defined()
}).test(
    'Recurring Billing Details',
    'Please complete your recurring payment method below',
    value => {
        if (value.total === null) {
            return true
        }

        const paymentMethod = value.paymentMethod
        if (paymentMethod === undefined || paymentMethod === null) {
            return false
        }

        if (
            paymentMethod.paymentSourceId === undefined ||
            paymentMethod.paymentSourceId === null ||
            paymentMethod.paymentSourceId.length < 1
        ) {
            return false
        }

        if (paymentMethod.type === 'StoredPaymentMethod') {
            if (paymentMethod.id === undefined) {
                return false
            }

            if (paymentMethod.id === '') {
                return false
            }

            return true
        }

        if (paymentMethod.type === 'CreditCard') {
            if (paymentMethod.token === undefined) {
                return false
            }

            if (paymentMethod.token === '') {
                return false
            }

            return true
        }

        if (paymentMethod.type === 'BankAccount') {
            if (
                paymentMethod.routingNumber === undefined ||
                paymentMethod.routingNumber === ''
            ) {
                return false
            }

            if (
                paymentMethod.accountNumber === undefined ||
                paymentMethod.accountNumber === ''
            ) {
                return false
            }

            if (
                paymentMethod.accountNumberConfirm === undefined ||
                paymentMethod.accountNumberConfirm === ''
            ) {
                return false
            }

            if (
                paymentMethod.accountNumber !==
                paymentMethod.accountNumberConfirm
            ) {
                return false
            }

            if (
                paymentMethod.accountType !== 'CHECKING' &&
                paymentMethod.accountType !== 'SAVINGS'
            ) {
                return false
            }

            return true
        }

        return false
    }
)

export function RecurringInformationInitialValues(
    recurringTotal: Decimal | null
) {
    return {
        total: recurringTotal === null ? null : recurringTotal.toFixed(2),
        paymentMethod: null
    }
}

export const recurringInformationSchema = toTypedSchema(
    RecurringInformationSchema
)
export type RecurringInformationSchemaType =
    typeof recurringInformationSchema extends TypedSchema<
        unknown,
        infer TOutput
    >
        ? TOutput
        : never
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import PrimeButton from 'primevue/button'
import { useField } from 'vee-validate'
import NewRecurringMethod from '~/components/Checkout/Sections/Payment/NewRecurringMethod.vue'
import {
    NewBankAccountKey,
    type PaymentMethodProperties
} from '~/components/Checkout/Sections/Payment/payment-method'
import PaymentMethodOption from '~/components/Checkout/Sections/Payment/PaymentMethodOption.vue'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const props = defineProps<{
    customer: FragmentType<typeof RecurringInformationCustomerFragment> | null
    facility: FragmentType<typeof RecurringInformationFacilityFragment>
    recurringTotal: Decimal
    editing: boolean
}>()

const emit = defineEmits<{
    (e: 'update:editing', payload: boolean): void
}>()

const facilityData = computed(() =>
    getFragmentData(RecurringInformationFacilityFragment, props.facility)
)

const customerData = computed(() =>
    props.customer === null
        ? null
        : getFragmentData(RecurringInformationCustomerFragment, props.customer)
)

const storedPaymentMethods = computed<PaymentMethodProperties[]>(() => {
    if (customerData.value === null) {
        return []
    }

    return customerData.value.storedPaymentMethods
        .filter(paymentMethod => !paymentMethod.migrationRequired)
        .map(paymentMethod => {
            const mapped: PaymentMethodProperties = {
                key: paymentMethod.id,
                paymentSourceId: paymentMethod.paymentSource.id,
                displayInfo: {
                    isCard: paymentMethod.isCreditCard,
                    brand: paymentMethod.formattedBrand,
                    lastFour: paymentMethod.formattedMaskedAccount,
                    accountExpirationDate: paymentMethod.accountExpirationDate,
                    lastUsedDate: paymentMethod.lastUsedDate
                },
                paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
                data: {
                    type: 'StoredPaymentMethod',
                    id: paymentMethod.id,
                    isCustomerDefault: paymentMethod.isCustomerDefault,
                    migrationRequired: paymentMethod.migrationRequired
                }
            }

            return mapped
        })
})

const selectedToken = computed(() => {
    if (recurring.value.paymentMethod === null) {
        return ''
    }

    return recurring.value.paymentMethod.key
})

const selectedPaymentMethod = computed<PaymentMethodProperties | null>(() => {
    if (selectedToken.value === '') {
        return null
    }

    if (
        addedPaymentMethod.value !== null &&
        addedPaymentMethod.value.key === selectedToken.value
    ) {
        return addedPaymentMethod.value
    }

    return (
        storedPaymentMethods.value.find(storedPaymentMethod => {
            return storedPaymentMethod.key === selectedToken.value
        }) ?? null
    )
})

const displayAllPaymentMethods = ref(false)
// When we stop editing, stop displaying all payment methods
watch(
    () => props.editing,
    newValue => {
        if (!newValue) {
            displayAllPaymentMethods.value = false
        }
    }
)

const addedPaymentMethod = ref<PaymentMethodProperties | null>(null)
const paymentMethods = computed(() => {
    return storedPaymentMethods.value.filter(paymentMethod => {
        return paymentMethod.key !== selectedToken.value
    })
})

const { value: recurring, errorMessage: errorRecurring } =
    useField<RecurringInformationSchemaType>('recurring', '', {
        validateOnValueUpdate: false,
        keepValueOnUnmount: true,
        initialValue: undefined
    })

const storedPaymentInformation = computed(() => {
    // If we have a customer, get the card information from that fragment
    if (customerData.value !== null) {
        return getFragmentData(
            StoredPaymentInformationFacilityFragment,
            customerData.value.newStoredPaymentInformation.facility
        )
    } else {
        return getFragmentData(
            StoredPaymentInformationFacilityFragment,
            facilityData.value
        )
    }
})

const showDefaultPaymentMethodNote = computed(() => {
    if (selectedPaymentMethod.value === null) {
        return true
    }

    if (selectedPaymentMethod.value.data.type !== 'StoredPaymentMethod') {
        return true
    }

    if (selectedPaymentMethod.value.data.isCustomerDefault) {
        return false
    }

    return true
})

const paymentSources = computed(() => {
    const creditCard =
        storedPaymentInformation.value.cpxCreditCardStoredPaymentSource
    const bankAccount =
        storedPaymentInformation.value.cpxBankAccountStoredPaymentSource

    const sources = []
    if (creditCard !== null) {
        sources.push(creditCard)
    }
    if (bankAccount !== null) {
        sources.push(bankAccount)
    }

    if (sources.length === 0) {
        throw new Error('No payment sources')
    }

    return sources
})

const addingPaymentMethod = ref(false)
function handleAddPaymentMethod(): void {
    // Wipe the current selection
    handleSelectPaymentMethod(null)
    addingPaymentMethod.value = true
}

function handleCancelAddPaymentMethod(): void {
    // Wipe the current selection
    handleSelectPaymentMethod(storedPaymentMethods.value[0])
    addingPaymentMethod.value = false
    displayAllPaymentMethods.value = true
}

function handleEditButton() {
    emit('update:editing', true)
}

function handleRecurringPaymentMethod(
    paymentMethod: PaymentMethodProperties | null
): void {
    if (paymentMethod === null) {
        addedPaymentMethod.value = null
    } else {
        addedPaymentMethod.value = paymentMethod
    }
}

function handleSelectPaymentMethod(
    paymentMethod: PaymentMethodProperties | null
) {
    // If we select an existing payment method when adding a new payment method
    // remove the payment method form
    if (paymentMethod !== null && addingPaymentMethod.value) {
        addingPaymentMethod.value = false
    }
    if (paymentMethod !== null && displayAllPaymentMethods.value) {
        displayAllPaymentMethods.value = false
    }

    let recurringPaymentMethod = null
    if (paymentMethod !== null) {
        if (paymentMethod.data.type === 'StoredPaymentMethod') {
            recurringPaymentMethod = {
                type: 'StoredPaymentMethod',
                key: paymentMethod.data.id,
                paymentSourceId: paymentMethod.paymentSourceId,
                paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
                displayInfo: { ...paymentMethod.displayInfo },
                id: paymentMethod.data.id,
                object: paymentMethod
            }
        } else if (paymentMethod.data.type === 'CardpointeCreditCard') {
            recurringPaymentMethod = {
                type: 'CreditCard',
                key: paymentMethod.data.token,
                paymentSourceId: paymentMethod.paymentSourceId,
                paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
                displayInfo: { ...paymentMethod.displayInfo },
                token: paymentMethod.data.token,
                object: paymentMethod
            }
        } else if (paymentMethod.data.type === 'Stripe') {
            recurringPaymentMethod = {
                type: 'CreditCard',
                key: paymentMethod.data.token,
                paymentSourceId: paymentMethod.paymentSourceId,
                paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
                displayInfo: { ...paymentMethod.displayInfo },
                token: paymentMethod.data.token,
                object: paymentMethod
            }
        } else {
            recurringPaymentMethod = {
                type: 'BankAccount',
                key: NewBankAccountKey,
                paymentSourceId: paymentMethod.paymentSourceId,
                paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
                displayInfo: { ...paymentMethod.displayInfo },
                routingNumber: paymentMethod.data.routingNumber,
                accountNumber: paymentMethod.data.accountNumber,
                accountNumberConfirm: paymentMethod.data.accountNumberConfirm,
                accountType: paymentMethod.data.accountType,
                object: paymentMethod
            }
        }
    }

    recurring.value = {
        total: recurring.value.total,
        paymentMethod: recurringPaymentMethod
    }
}

const { isProcessing } = useCheckoutFormProcessing()

const newRecurringMethodRef = ref<InstanceType<typeof NewRecurringMethod>>()
async function submitRecurringForm() {
    if (
        newRecurringMethodRef.value !== undefined &&
        newRecurringMethodRef.value !== null
    ) {
        await newRecurringMethodRef.value.submitRecurringForm()
    }

    if (addedPaymentMethod.value !== null) {
        addingPaymentMethod.value = false
        handleSelectPaymentMethod(addedPaymentMethod.value)
    }
}

defineExpose({
    submitRecurringForm
})

// When we load for first time, select the first payment method
if (storedPaymentMethods.value.length > 0) {
    handleSelectPaymentMethod(storedPaymentMethods.value[0])
}
// Otherwise, we need to add a new payment method
else {
    addingPaymentMethod.value = true
}
</script>

<style scoped>
.collapse-enter-active {
    transition: all 0.3s ease-out;
    animation: collapse reverse 500ms ease;
}

.collapse-leave-active {
    animation: collapse 500ms ease;
    transition: all 0.5s cubic-bezier(1, 0.5, 0.8, 1);
}

@keyframes collapse {
    from {
        max-height: 200px;
    }
    to {
        max-height: 0px;
    }
}

.collapse-enter-to,
.collapse-leave-from {
    height: 100%;
    overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
    opacity: 0;
    height: 0;
    overflow: hidden;
}
</style>
